import { Component, Prop, Vue, Watch, toNative } from "vue-facing-decorator";

import { createConversionStore, makeConversionProvider, type IConversionStore, type ConversionProvider } from "./ConversionStore";

@Component
export class ConversionStoreRegistration<S, T> extends Vue {
	@Prop({ type: String, required: true }) protected store!: string;

	// Seeded data that has not been pulled through an AJAX call.
	@Prop({ required: false, type: Object, default: () => ({}) }) protected initialConversions!: { [K: string]: T };
	@Prop({ required: false, default: null }) protected conversionUrl!: string | ConversionProvider<S, T> | null;

	// When set, the conversions are not reset when the conversion store is recreated.
	// You might want to set this when a conversion store is used in many places
	// on a page but the contained conversions never change, except when they are
	// invalidated explicitly. When set to true, initialConversions will be loaded
	// only on store registration (e.g. when this component is added to page for the first time)
	// and will be ignored afterwards.
	@Prop({ required: false, type: Boolean, default: false }) shared!: boolean;

	protected created() {
		this.getStore();
	}

	private getStore(): IConversionStore<S, T> {
		let conversionProvider = this.conversionProvider;
		if (conversionProvider === null) {
			conversionProvider = (keys: S[]) => {
				keys;
				throw Error("No conversions can be loaded dynamically for this list since no conversion-url has been specified.");
			};
		}
		return createConversionStore(this.store, conversionProvider, this.initialConversions, true);
	}

	@Watch("conversionProvider")
	protected conversionProviderChanged(): void {
		this.getStore().reset(this.conversionProvider);
	}

	@Watch("store")
	protected storeChange(store: string, old: string): void {
		// see ListStoreRegistration for an explanation why this is not possible.
		throw Error(`Store name changed from ${old} to ${store}. You cannot change the name of a registered store. Make sure that the store name is bound to the lifetime of the parent.`);
	}

	private get conversionProvider() {
		if (typeof this.conversionUrl === "string") {
			return makeConversionProvider<S, T>(this.conversionUrl);
		}
		if (this.conversionUrl === null) {
			return (keys: S[]) => Promise.resolve(keys.map((key: any) => {
				if (key in this.initialConversions) {
					return this.initialConversions[key];
				}
				throw Error("No conversions except for the initial-conversions can be loaded dynamically for this list since no conversion-url has been specified.");
			}));
		}
		return this.conversionUrl;
	}

	protected render() {}
}

export default toNative(ConversionStoreRegistration);
